// Shared "float a panel next to an anchor element" math, used by both the
// tooltip directive and the guided-tour overlay so they behave identically.
export type VwPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

export interface VwFloatResult {
  top: number;
  left: number;
  placement: Exclude<VwPlacement, 'auto'>;
}

const GAP = 10;

/**
 * Positions a floating panel of size (panelW x panelH) next to anchorRect,
 * preferring `placement`. In 'auto' mode (or when the preferred side would
 * overflow the viewport), picks whichever side actually has room, falling
 * back to the side with the most available space.
 */
export function computeFloatingPosition(
  anchorRect: DOMRect,
  panelW: number,
  panelH: number,
  placement: VwPlacement,
  viewportW: number = window.innerWidth,
  viewportH: number = window.innerHeight,
): VwFloatResult {
  const space = {
    top: anchorRect.top,
    bottom: viewportH - anchorRect.bottom,
    left: anchorRect.left,
    right: viewportW - anchorRect.right,
  };

  const fits = (side: 'top' | 'bottom' | 'left' | 'right'): boolean => {
    if (side === 'top') { return space.top >= panelH + GAP; }
    if (side === 'bottom') { return space.bottom >= panelH + GAP; }
    if (side === 'left') { return space.left >= panelW + GAP; }
    return space.right >= panelW + GAP;
  };

  let side: 'top' | 'bottom' | 'left' | 'right';
  if (placement !== 'auto' && fits(placement)) {
    side = placement;
  } else if (placement !== 'auto' && !fits(placement)) {
    // preferred side doesn't fit — try its opposite, then fall back to auto
    const opposite: Record<string, 'top' | 'bottom' | 'left' | 'right'> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
    const alt = opposite[placement];
    side = fits(alt) ? alt : biggestSide(space);
  } else {
    side = (['bottom', 'top', 'right', 'left'] as const).find(fits) || biggestSide(space);
  }

  let top: number;
  let left: number;

  if (side === 'top' || side === 'bottom') {
    top = side === 'top' ? anchorRect.top - panelH - GAP : anchorRect.bottom + GAP;
    left = anchorRect.left + anchorRect.width / 2 - panelW / 2;
  } else {
    left = side === 'left' ? anchorRect.left - panelW - GAP : anchorRect.right + GAP;
    top = anchorRect.top + anchorRect.height / 2 - panelH / 2;
  }

  // clamp so the panel never renders off-screen
  left = Math.max(8, Math.min(left, viewportW - panelW - 8));
  top = Math.max(8, Math.min(top, viewportH - panelH - 8));

  return { top, left, placement: side };
}

function biggestSide(space: { top: number; bottom: number; left: number; right: number }): 'top' | 'bottom' | 'left' | 'right' {
  return (Object.keys(space) as Array<keyof typeof space>).reduce((a, b) => (space[b] > space[a] ? b : a), 'bottom');
}
